// src/Tracking.tsx
import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { useTheme, useTrack, useManualUpload } from "../../hooks/index";
import { Row, Column, Form, Container } from "../../components/grid/index";
import Header from "../../components/HeaderComponent";
import { AuthContext } from "../../components/AuthContextComponent";
import TextField from "../../components/textfield/TextField";
import Button from "../../components/button/Button";
import Card from "../../components/card/Card";
import Image from "../../components/image/Image";
import { PiPersonSimpleRunFill, PiPersonSimpleWalk } from "react-icons/pi";
import { GiFinishLine } from "react-icons/gi";
import { FaFileAlt } from "react-icons/fa";
import SecurityQuestion from "../../components/SecurityQuestion";
import { SecurityQuestionRequestArray } from "../../lib/interfaces/index";
import { forgetPassword } from "../../api/FetchAPI";
import LoadingComponent from "../../components/loading/Loading";
import { formatDate, FormatNumber } from "../../lib/formatter/Formatter";
import { AutoLogout } from "../../components/AutoLogoutComponent";
import UpdatePassword from "../../components/updatepassword/UpdatePassword";
import { STATUS } from "../../lib/enums/Type";
import { GetApproversInfo } from "../../lib/interfaces/ForApprovalInfoInterface";
import useUserLogin from "../../hooks/UserLoginhook";
import useHeadComponent from "../../hooks/HeaderHook";
import { showAlert } from "../../promptMessages/PromptMessages";
import { ALERTMSG } from "../../lib/enums";

interface PageProps {
  $color: string;
}

interface StatusProps {
  $approvalStatus: string;
  $available?: boolean;
}

const Tracking: React.FC = () => {
  const userInfo = useContext(AuthContext);
  const { username, setPartnerName } = useContext(AuthContext);
  const {
    approverInfo,
    loading,
    batchNumber,
    trackinfo,
    handleInputTrack,
    batchNumberLookUp,
  } = useTrack();

  const color = userInfo.userInfo?.theme === "DARK" ? "#fafbfc" : "black";
  const { openOTP, selectedPartner } = useManualUpload();
  const { Balance } = useHeadComponent(selectedPartner);
  const { customColor } = useUserLogin();
  const { theme } = useTheme();
  const [redirectModal, setRedirectModal] = useState(false);
  const [currentQuestions, setCurrentQuestions] =
    useState<SecurityQuestionRequestArray | null>(null);

  const [isloading, setLoading] = useState(true);
  const themeBackground = userInfo?.userInfo?.theme === "DARK" ? "#24292e" : "";
  const themeTextColor = userInfo?.userInfo?.theme === "DARK" ? "#fafbfc" : "";
  const [renderContent, setRenderContent] = useState<React.ReactNode>(null);
  const { pwdUpdate, category } = userInfo.userInfo || {};

  const redirectState = !!(redirectModal && category === "EXTERNAL");

  const firstTimeLoginState = !!(pwdUpdate && category === "EXTERNAL");

  useEffect(() => {
    const fetchCurrentUserQuestion = async () => {
      // try {
      const response: any = await forgetPassword(username);

      const { respCode, respMsg } = response;

      if (respCode === 2000) {
        if (
          response.questionInfo === null ||
          response.questionInfo.length < 2
        ) {
          setCurrentQuestions(null);
          setRedirectModal(true);
        } else {
          setCurrentQuestions(response.questionInfo);
        }
      } else {
        showAlert(
          ALERTMSG.SOMEWENTWRONG,
          `${respMsg} (${respCode})`,
          "error",
          customColor,
          themeBackground,
          themeTextColor,
        );
      }
      // } catch (error: any) {
      //   FetchErr(error, themeBackground, customColor, themeTextColor);
      // }
    };

    let count = 0;

    if (count === 0) {
      const timer = setTimeout(() => {
        fetchCurrentUserQuestion();
      }, 1000);
      count++;
      return () => clearTimeout(timer);
    }
  }, [username]);

  setPartnerName("");
  useEffect(() => {
    setTimeout(async () => {
      setLoading(false);
      if (pwdUpdate && category === STATUS.EXTERNAL) {
        setRenderContent(<UpdatePassword />);
      } else if (redirectModal && category === STATUS.EXTERNAL) {
        setRenderContent(<SecurityQuestion />);
      } else {
        setRenderContent(null);
      }
    }, 2000);
  }, []);

  //Status text color
  const status =
    trackinfo?.batchNumberInfo[0].approvalStatus === STATUS.APPROVED
      ? "green-500"
      : trackinfo?.batchNumberInfo[0].approvalStatus === STATUS.PENDING
        ? "yellow-500"
        : trackinfo?.batchNumberInfo[0].approvalStatus === STATUS.REJECTED
          ? "custom-default"
          : "";

  //check if account has no approval level
  const noApprovalLevel =
    trackinfo?.batchNumberInfo?.[0]?.batchNumberStatusInfo === null &&
    trackinfo?.batchNumberInfo?.[0]?.status === STATUS.AVAILABLE
      ? true
      : false;

  const approvers = approverInfo?.approverInfo || [];

  //for multiple approvers
  const groupedByApprovalLevel = approvers.reduce<
    Record<number, GetApproversInfo["approverInfo"]>
  >((acc, approver) => {
    const level = approver.approverLevel;
    if (!acc[level]) {
      acc[level] = [];
    }
    acc[level].push(approver);
    return acc;
  }, {});

  const batchInfo = trackinfo?.batchNumberInfo?.[0];
  const isFullyApproved = batchInfo?.approvalStatus === STATUS.APPROVED;
  const approvalLevels = Array.from(
    new Set([
      ...(isFullyApproved
        ? []
        : approvers.map((approver) => approver.approverLevel)),
      ...(batchInfo?.batchNumberStatusInfo?.map(
        (entry) => entry.approverLevel,
      ) || []),
    ]),
  )
    .filter((level) => level > 0)
    .sort((a, b) => a - b);
  const timelineLevels =
    approvalLevels.length || isFullyApproved
      ? approvalLevels
      : Array.from(
          { length: batchInfo?.approvers || 0 },
          (_, index) => index + 1,
        );

  return (
    <Page $color={color}>
      <Header Balance={Balance} theme={theme}></Header>
      <AutoLogout
        openOTP={openOTP}
        redirectModal={redirectState}
        redirectChangePassword={firstTimeLoginState}
      />
      <PageContent>
        {isloading ? (
          <InitialLoading>
            <LoadingComponent customColor={customColor} />
          </InitialLoading>
        ) : (
          <>
            {" "}
            <Container>
              <Column itemPosition="center">
                <TrackingCard>
                  <Column>
                    <Row>
                      <Column gap={5} itemPosition="center">
                        <Form onSubmit={batchNumberLookUp}>
                          <TextField
                            label="Batch Number"
                            type={"text"}
                            value={batchNumber}
                            onChange={handleInputTrack}
                          ></TextField>
                          <Button
                            width="100px"
                            label="Track"
                            rounded
                            variant="btn-danger"
                            type="submit"
                            isTrack
                            customColor={customColor}
                          ></Button>
                        </Form>
                      </Column>
                    </Row>
                  </Column>
                </TrackingCard>
                {loading ? (
                  <TrackLoading>
                    <LoadingComponent customColor={customColor} />
                  </TrackLoading>
                ) : trackinfo?.respCode === 2000 &&
                  trackinfo.batchNumberInfo?.length > 0 ? (
                  <TrackResults>
                    <TimelineSection>
                      <Timeline>
                        <TimelineParticipant>
                          <TimelineAvatar
                            height={75}
                            width={75}
                            rounded="full"
                            backgroundcolor={"#4CCD99"}
                            isApprovalStatus
                          >
                            <StartFileIcon />
                          </TimelineAvatar>
                          <TimelineConnector
                            height={1}
                            width={135}
                            gradientColor={"to right,#feb47b,#4CCD99, #feb47b"}
                            isApprovalStatus
                          />

                          <UploadedBy>
                            {trackinfo.batchNumberInfo[0]
                              .batchNumberStatusInfo === null
                              ? ""
                              : "Uploaded By:"}
                            <span>
                              {trackinfo.batchNumberInfo &&
                                trackinfo.batchNumberInfo.length > 0 &&
                                trackinfo.batchNumberInfo[0]
                                  .batchNumberStatusInfo &&
                                trackinfo.batchNumberInfo[0]
                                  .batchNumberStatusInfo.length > 0 &&
                                trackinfo.batchNumberInfo[0]
                                  .batchNumberStatusInfo[0]?.userFName}
                            </span>
                          </UploadedBy>
                        </TimelineParticipant>
                        {timelineLevels.map((level) => {
                          const decision =
                            batchInfo?.batchNumberStatusInfo?.find(
                              (entry) =>
                                entry.approverLevel === level &&
                                (entry.status === STATUS.APPROVED ||
                                  entry.status === STATUS.REJECTED),
                            );
                          const isApproved = decision?.status;
                          const decisionDate =
                            isApproved === STATUS.APPROVED
                              ? decision?.approvedDate
                              : decision?.rejectedDate;
                          return (
                            <TimelineParticipant key={level}>
                              <TimelineAvatar
                                width={75}
                                height={75}
                                rounded="full"
                                isApproved={false}
                                backgroundcolor={
                                  isApproved === "APPROVED" || noApprovalLevel
                                    ? "#4CCD99"
                                    : isApproved === "REJECTED"
                                      ? "#C01217"
                                      : ""
                                }
                              >
                                {isApproved === "APPROVED" ||
                                noApprovalLevel ? (
                                  <RunnerIcon />
                                ) : isApproved === "REJECTED" ? (
                                  <RejectedIcon />
                                ) : (
                                  <PendingIcon />
                                )}
                              </TimelineAvatar>
                              <TimelineConnector
                                height={1}
                                width={135}
                                gradientColor={
                                  isApproved === "APPROVED" || noApprovalLevel
                                    ? "to right,#feb47b,#4CCD99, #feb47b"
                                    : ""
                                }
                              />

                              <ApproverNames
                                role="group"
                                aria-label={`Approval level ${level}`}
                              >
                                {decision ? (
                                  <>
                                    {isApproved === STATUS.APPROVED ? (
                                      <ApprovedName>
                                        {decision.userFName}
                                      </ApprovedName>
                                    ) : (
                                      <RejectedName>
                                        {decision.userFName}
                                      </RejectedName>
                                    )}
                                    {decisionDate ? (
                                      <DecisionTimestamp
                                        dateTime={decisionDate}
                                        aria-label="Decision timestamp"
                                      >
                                        {formatDate(decisionDate)}
                                      </DecisionTimestamp>
                                    ) : (
                                      <span>Timestamp unavailable</span>
                                    )}
                                  </>
                                ) : (
                                  groupedByApprovalLevel[level]?.map(
                                    (approver) => (
                                      <ApproverName key={approver.userId}>
                                        {approver.userFName}
                                      </ApproverName>
                                    ),
                                  )
                                )}
                              </ApproverNames>
                            </TimelineParticipant>
                          );
                        })}

                        <TimelineEnd>
                          <TimelineAvatar
                            height={75}
                            width={75}
                            rounded="full"
                            backgroundcolor={
                              trackinfo.batchNumberInfo[0].approvalStatus ===
                                "APPROVED" || noApprovalLevel
                                ? "#4CCD99"
                                : ""
                            }
                            isApprovalStatus
                          >
                            <FinishIcon
                              $complete={
                                trackinfo.batchNumberInfo[0].approvalStatus ===
                                  "APPROVED" || noApprovalLevel
                              }
                            />
                          </TimelineAvatar>
                          <CompletionText
                            $approvalStatus={status}
                            $available={noApprovalLevel}
                          >
                            {trackinfo.batchNumberInfo[0].approvalStatus ===
                              "APPROVED" || noApprovalLevel
                              ? "COMPLETED"
                              : ""}
                          </CompletionText>
                        </TimelineEnd>
                      </Timeline>
                    </TimelineSection>
                    <MetricsGrid>
                      <Metric>
                        <MetricLabel>Total Transaction:</MetricLabel>
                        <MetricValue>
                          {trackinfo.batchNumberInfo[0].total}
                        </MetricValue>
                      </Metric>
                      <Metric>
                        <MetricLabel>Amount:</MetricLabel>
                        <AmountValue>
                          {trackinfo.batchNumberInfo[0].origCurrency}{" "}
                          {FormatNumber(
                            trackinfo.batchNumberInfo[0].totalOrigPrincipal,
                          )}
                        </AmountValue>
                      </Metric>
                      <Metric>
                        <MetricLabel>Status:</MetricLabel>
                        <StatusValue
                          $approvalStatus={status}
                          $available={noApprovalLevel}
                        >
                          {noApprovalLevel
                            ? "AVAILABLE"
                            : trackinfo.batchNumberInfo[0].approvalStatus}
                        </StatusValue>
                      </Metric>
                      <Metric>
                        <MetricLabel>Date Created:</MetricLabel>
                        <DateValue>
                          {formatDate(trackinfo.batchNumberInfo[0].transDate)}
                        </DateValue>
                      </Metric>
                      {trackinfo.batchNumberInfo[0].approvalStatus ===
                      "APPROVED" ? (
                        <Metric>
                          <MetricLabel>Date Approved:</MetricLabel>
                          <DateValue>
                            {formatDate(
                              trackinfo.batchNumberInfo[0].approvedDate,
                            )}
                          </DateValue>
                        </Metric>
                      ) : trackinfo.batchNumberInfo[0].approvalStatus ===
                        "REJECTED" ? (
                        <Metric>
                          <MetricLabel>Date Rejected:</MetricLabel>
                          <DateValue>
                            {formatDate(
                              trackinfo.batchNumberInfo[0].rejectedDate,
                            )}
                          </DateValue>
                        </Metric>
                      ) : null}
                    </MetricsGrid>
                  </TrackResults>
                ) : null}
              </Column>
            </Container>
          </>
        )}

        {renderContent}
      </PageContent>
    </Page>
  );
};

export default Tracking;

const Page = styled.div<PageProps>`
  color: ${({ $color }) => $color};
  font-family: "Poppins", sans-serif;
`;

const PageContent = styled.main`
  max-width: 1240px;
  margin: 0 auto;
`;

const InitialLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 1240px;
  height: 100%;
  margin: 0 auto;
  padding: 24rem 1rem;
`;

const TrackingCard = styled(Card)`
  width: 16rem;
  height: 7rem;
  align-items: center;

  @media (min-width: 640px) {
    width: 16rem;
    height: 8rem;
  }

  @media (min-width: 768px) {
    width: 18rem;
    height: 8rem;
  }

  @media (min-width: 1024px) {
    width: 24rem;
    height: 11rem;
  }
`;

const TimelineAvatar = styled(Image)`
  width: var(--circle-size);
  height: var(--circle-size);
`;

const TimelineConnector = styled(Image)`
  width: var(--connector-size);
  align-self: center;
`;

const StartFileIcon = styled(FaFileAlt)`
  color: white;
  font-size: 1.125rem;
  transition: transform 1s;

  &:hover {
    transform: scale(1.1);
  }

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const RunnerIcon = styled(PiPersonSimpleRunFill)`
  color: white;
  font-size: 1.5rem;

  &:hover {
    transform: scale(1.1);
  }
`;

const RejectedIcon = styled(PiPersonSimpleWalk)`
  color: white;
  font-size: 1.5rem;

  &:hover {
    transform: scale(1.1);
  }
`;

const PendingIcon = styled(PiPersonSimpleWalk)`
  color: black;
  font-size: 1.5rem;

  &:hover {
    transform: scale(1.1);
  }
`;

const FinishIcon = styled(GiFinishLine)<{ $complete: boolean }>`
  color: ${({ $complete }) => ($complete ? "white" : "black")};
  font-size: 1.125rem;
  transition: transform 1s;

  &:hover {
    transform: scale(1.1);
  }

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const TrackLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 350px;
  font-family: "Poppins", sans-serif;
`;

const TrackResults = styled.section`
  width: 100%;
  margin-top: 2.5rem;
  padding: 1rem;
  border-radius: 0.25rem;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
`;

const TimelineSection = styled.div`
  margin-top: 2.5rem;
  overflow-x: auto;
`;

const Timeline = styled.div`
  --circle-size: 3rem;
  --connector-size: 2.5rem;
  display: flex;
  width: max-content;
  min-width: 100%;
  align-items: flex-start;
  justify-content: center;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 640px) {
    --circle-size: 3.5rem;
    --connector-size: 4rem;
  }

  @media (min-width: 768px) {
    --circle-size: 4rem;
    --connector-size: 5rem;
  }

  @media (min-width: 1024px) {
    --circle-size: 5rem;
    --connector-size: 6rem;
  }
`;

const TimelineParticipant = styled.div`
  display: grid;
  grid-template-columns: var(--circle-size) var(--connector-size);
  grid-template-rows: var(--circle-size) auto;
  align-items: center;
  flex-shrink: 0;
`;

const UploadedBy = styled.p`
  grid-column: 1;
  justify-self: center;
  display: flex;
  width: calc(var(--circle-size) + var(--connector-size) - 0.5rem);
  flex-direction: column;
  gap: 0.5rem;
  margin: 1.25rem 0 0;
  text-align: center;
  overflow-wrap: anywhere;
  font-size: 0.75rem;
`;

const ApproverNames = styled.div`
  grid-column: 1;
  justify-self: center;
  display: flex;
  width: calc(var(--circle-size) + var(--connector-size) - 0.5rem);
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  margin-top: 1.25rem;
  text-align: center;
  overflow-wrap: anywhere;
  font-size: 0.75rem;
`;

const DecisionTimestamp = styled.time`
  font-size: 0.625rem;
  line-height: 1.5;
`;

const ApproverName = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0 0.5rem;
`;

const ApprovedName = styled.span`
  width: 100%;
  padding: 0 0.5rem;
  color: #22c55e;
`;

const RejectedName = styled(ApprovedName)`
  color: #ef4444;
`;

const TimelineEnd = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`;

const getStatusColor = (status: string) => {
  switch (status) {
    case "green-500":
      return "#22c55e";
    case "yellow-500":
      return "#eab308";
    case "custom-default":
      return "#c01217";
    default:
      return "inherit";
  }
};

const CompletionText = styled.p<StatusProps>`
  margin-top: 1.25rem;
  color: ${({ $approvalStatus, $available }) =>
    $available ? "#22c55e" : getStatusColor($approvalStatus)};
  font-size: 0.75rem;

  @media (min-width: 768px) {
    font-size: 0.75rem;
  }
`;

const MetricsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4rem 2rem;
  margin-top: 5rem;
  transition: all 1s ease-in-out;

  @media (min-width: 1024px) {
    gap: 2rem;
  }
`;

const Metric = styled.div`
  display: flex;
  flex: 0 1 calc(50% - 1rem);
  flex-direction: column;
  align-items: center;
  min-width: 0;
  overflow-wrap: anywhere;

  @media (min-width: 1024px) {
    flex: 1 1 0;
  }
`;

const MetricLabel = styled.h2`
  width: 100%;
  text-align: center;
  font-size: 1rem;
`;

const MetricValue = styled.h2`
  width: 100%;
  height: 100%;
  padding: 0.5rem 0;
  text-align: center;
  font-weight: 600;
  font-size: 1rem;

  @media (min-width: 768px) {
    font-size: 1.25rem;
  }

  @media (min-width: 1024px) {
    font-size: 1.5rem;
  }
`;

const AmountValue = styled(MetricValue)`
  @media (min-width: 768px) {
    font-size: 1.125rem;
  }

  @media (min-width: 1024px) {
    font-size: 1.25rem;
  }
`;

const StatusValue = styled(MetricValue)<StatusProps>`
  padding: 0.5rem 0;
  color: ${({ $approvalStatus, $available }) =>
    $available ? "#22c55e" : getStatusColor($approvalStatus)};
  font-weight: 700;

  @media (min-width: 768px) {
    font-size: 1.125rem;
  }

  @media (min-width: 1024px) {
    font-size: 1.25rem;
  }
`;

const DateValue = styled.h2`
  width: 100%;
  height: 100%;
  padding: 0.875rem 0;
  text-align: center;
  font-size: 0.75rem;
`;
